/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input, Modal, message, Tag, Select, DatePicker,
         Popconfirm, Tooltip, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, SearchOutlined, UserOutlined,
         CarOutlined, HistoryOutlined, CheckOutlined, WarningOutlined,
         CloseOutlined, DashboardOutlined, DownloadOutlined, TableOutlined } from '@ant-design/icons';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip as RTooltip, Legend,
    ResponsiveContainer, LabelList
} from 'recharts';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FleetService from '../../services/FleetService';

const { Option }   = Select;
const { TabPane }  = Tabs;
const { RangePicker } = DatePicker;

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
    blue:'#1a5b9a', green:'#389e0d', orange:'#d46b08',
    red:'#cf1322', purple:'#722ed1', amber:'#faad14', grey:'#595959',
};
const PIE_C = [C.blue, C.green, C.orange, C.red, C.purple, C.amber];

const fmtD    = (v) => (!v || v === '0000-00-00') ? '—' : moment(v).format('DD MMM YYYY');
const fmtDays = (n) => n != null ? `${Number(n).toLocaleString()} day${n != 1 ? 's' : ''}` : '—';
const toN     = (v) => Number(v || 0);

// ─── Period Filter ────────────────────────────────────────────────────────────
const PRESETS = [
    { key:'this_month', label:'This Month',    from:() => moment().startOf('month'),           to:() => moment() },
    { key:'last_3m',    label:'Last 3 Months', from:() => moment().subtract(3,'months'),       to:() => moment() },
    { key:'this_year',  label:'This Year',     from:() => moment().startOf('year'),             to:() => moment() },
    { key:'last_year',  label:'Last Year',     from:() => moment().subtract(1,'year').startOf('year'), to:() => moment().subtract(1,'year').endOf('year') },
    { key:'all',        label:'All Time',      from:() => null,                                 to:() => null },
];
const DEFAULT_PERIOD = { preset:'this_year', from: moment().startOf('year').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') };

function PeriodFilter({ value, onChange }) {
    const cur = value || DEFAULT_PERIOD;
    const handlePreset = (preset) => {
        const p = PRESETS.find(x => x.key === preset);
        onChange({ preset, from: p.from() ? p.from().format('YYYY-MM-DD') : '', to: p.to() ? p.to().format('YYYY-MM-DD') : '' });
    };
    return (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            <span style={{ fontSize:11, color:'#888' }}>Period:</span>
            {PRESETS.map(p => (
                <button key={p.key} onClick={() => handlePreset(p.key)} style={{
                    padding:'3px 10px', border:'1px solid', borderRadius:12, cursor:'pointer',
                    fontSize:11, fontWeight: cur.preset===p.key ? 600 : 400,
                    background: cur.preset===p.key ? '#1a3a5c' : '#fff',
                    borderColor: cur.preset===p.key ? '#1a3a5c' : '#d9d9d9',
                    color: cur.preset===p.key ? '#fff' : '#595959',
                }}>{p.label}</button>
            ))}
            <RangePicker size="small" style={{ width:210 }}
                value={cur.preset==='custom' && cur.from && cur.to ? [moment(cur.from), moment(cur.to)] : null}
                onChange={([s, e] = []) => onChange({ preset:'custom', from: s ? s.format('YYYY-MM-DD') : '', to: e ? e.format('YYYY-MM-DD') : '' })}
                format="DD MMM YYYY" placeholder={['Custom from','Custom to']} />
            {cur.from && cur.to && (
                <span style={{ fontSize:10, color:'#1a5b9a', background:'#e6f4ff', padding:'2px 8px', borderRadius:8, whiteSpace:'nowrap' }}>
                    {moment(cur.from).format('DD MMM YYYY')} → {moment(cur.to).format('DD MMM YYYY')}
                </span>
            )}
        </div>
    );
}

function filterByPeriod(data, period, dateField) {
    if (!period || period.preset === 'all' || (!period.from && !period.to)) return data;
    return data.filter(r => {
        const d = r[dateField];
        if (!d || d === '0000-00-00') return false;
        const m = moment(d);
        if (period.from && m.isBefore(moment(period.from), 'day')) return false;
        if (period.to   && m.isAfter(moment(period.to),   'day')) return false;
        return true;
    });
}

// ─── Anomaly Detector ─────────────────────────────────────────────────────────
function detectAnomalies(assignments, drivers) {
    const issues = [];

    // Driver assigned to multiple vehicles simultaneously
    const activeByDriver = {};
    assignments.filter(a => a.status === 'Active').forEach(a => {
        if (!activeByDriver[a.driver_id]) activeByDriver[a.driver_id] = [];
        activeByDriver[a.driver_id].push(a);
    });
    Object.values(activeByDriver).forEach(list => {
        if (list.length > 1) {
            issues.push({ type:'error', msg:`${list[0].driver_name} is assigned to ${list.length} vehicles simultaneously (${list.map(a=>a.registration_no).join(', ')})` });
        }
    });

    // Very long active assignments (> 365 days)
    assignments.filter(a => a.status === 'Active').forEach(a => {
        const days = toN(a.days_assigned);
        if (days > 365) issues.push({ type:'warn', msg:`${a.driver_name} → ${a.registration_no} assignment is ${days} days old — may need review` });
    });

    // Expired driver licences on active assignments
    assignments.filter(a => a.status === 'Active').forEach(a => {
        const drv = drivers.find(d => d.id === a.driver_id);
        if (drv && toN(drv.licence_days_remaining) < 0) {
            issues.push({ type:'error', msg:`${a.driver_name} has an EXPIRED licence but is still actively assigned to ${a.registration_no}` });
        }
    });

    // Vehicles with no primary driver assigned
    // (handled in KPI, not anomaly — skip here)

    return issues;
}

// ─── Mini Dashboard ───────────────────────────────────────────────────────────
function AssignmentsDashboard({ assignments, drivers, vehicles, period, onPeriodChange }) {
    const periodData = filterByPeriod(assignments, period, 'assigned_from');

    const anomalies = useMemo(() => detectAnomalies(assignments, drivers), [assignments, drivers]);

    const byFacility = useMemo(() => {
        const m = {};
        periodData.forEach(a => { m[a.facility||'Unknown'] = (m[a.facility||'Unknown']||0)+1; });
        return Object.entries(m).map(([name,count]) => ({name,count})).sort((a,b)=>b.count-a.count).slice(0,8);
    }, [periodData]);

    const byRole = useMemo(() => [
        { name:'Primary', value: periodData.filter(a=>(a.role||'Primary')==='Primary').length },
        { name:'Relief',  value: periodData.filter(a=>a.role==='Relief').length },
    ].filter(d=>d.value>0), [periodData]);

    const licenceStatus = useMemo(() => {
        const expired  = drivers.filter(d => toN(d.licence_days_remaining) < 0).length;
        const expiring = drivers.filter(d => toN(d.licence_days_remaining) >= 0 && toN(d.licence_days_remaining) < 30).length;
        const valid    = drivers.length - expired - expiring;
        return [
            { name:'Valid',    value: valid,    fill: C.green  },
            { name:'Expiring', value: expiring, fill: C.amber  },
            { name:'Expired',  value: expired,  fill: C.red    },
        ].filter(d => d.value > 0);
    }, [drivers]);

    const monthlyTrend = useMemo(() => {
        const m = {};
        assignments.forEach(a => {
            if (!a.assigned_from || a.assigned_from === '0000-00-00') return;
            const mo = moment(a.assigned_from).format('MMM YY');
            m[mo] = (m[mo]||0)+1;
        });
        return Object.entries(m).slice(-8).map(([month,count]) => ({month,count}));
    }, [assignments]);

    const unassigned = vehicles.filter(v => !v.current_driver_name);
    const avgDays    = useMemo(() => {
        const ended = assignments.filter(a => a.status === 'Ended' && toN(a.days_assigned) > 0);
        if (!ended.length) return null;
        return (ended.reduce((s,a) => s + toN(a.days_assigned), 0) / ended.length).toFixed(0);
    }, [assignments]);

    return (
        <div style={{ background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:20 }}>

            {/* Period filter */}
            <div style={{ marginBottom:12 }}>
                <PeriodFilter value={period} onChange={onPeriodChange} />
            </div>

            {/* Anomaly banner */}
            {anomalies.length > 0 && (
                <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:6,
                    padding:'8px 14px', marginBottom:14 }}>
                    <div style={{ fontWeight:700, color:'#cf1322', fontSize:12, marginBottom:6 }}>
                        <WarningOutlined style={{ marginRight:6 }} />
                        {anomalies.length} irregularit{anomalies.length>1?'ies':'y'} detected
                    </div>
                    {anomalies.map((a,i) => (
                        <div key={i} style={{ fontSize:11, display:'flex', gap:6, marginBottom:3 }}>
                            <Tag color={a.type==='error'?'red':'orange'} style={{ fontSize:9, margin:0 }}>
                                {a.type==='error'?'Error':'Warning'}
                            </Tag>
                            {a.msg}
                        </div>
                    ))}
                </div>
            )}

            {/* Insight pills */}
            <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
                {[
                    { label:'In Period',         value: periodData.length,                           color: C.blue   },
                    { label:'Avg Assignment',    value: avgDays ? `${avgDays} days` : '—',           color: C.orange },
                    { label:'Unassigned Vehicles',value: unassigned.length,                          color: unassigned.length > 10 ? C.red : C.amber },
                    { label:'Expired Licences',  value: drivers.filter(d=>toN(d.licence_days_remaining)<0).length, color: C.red },
                    { label:'Multi-Vehicle Drivers', value: Object.values(
                        assignments.filter(a=>a.status==='Active').reduce((m,a) => {
                            m[a.driver_id] = (m[a.driver_id]||0)+1; return m;
                        }, {})).filter(c=>c>1).length, color: C.purple },
                ].map(({label,value,color}) => (
                    <div key={label} style={{ background:'#fff', border:'1px solid #e8e8e8',
                        borderRadius:6, padding:'6px 12px', borderLeft:`3px solid ${color}` }}>
                        <div style={{ fontSize:10, color:'#888' }}>{label}</div>
                        <div style={{ fontSize:16, fontWeight:700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* Charts row */}
            <div style={{ display:'grid', gridTemplateColumns:'1.6fr 1fr 1fr 1fr', gap:12 }}>

                {/* Assignments by facility */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>
                        Assignments by Facility (top 8)
                    </div>
                    <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={byFacility} layout="vertical"
                            margin={{ top:0, right:35, left:130, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:9 }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:9 }} width={125} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.blue} radius={[0,3,3,0]}>
                                <LabelList dataKey="count" position="right" style={{ fontSize:9 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Role pie */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>Primary vs Relief</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                            <Pie data={byRole} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={55}
                                label={({name,percent}) => `${name} ${(percent*100).toFixed(0)}%`}
                                labelLine={false}>
                                <Cell fill={C.blue} />
                                <Cell fill={C.purple} />
                            </Pie>
                            <RTooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Licence status pie */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>Driver Licence Status</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                            <Pie data={licenceStatus} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={55}
                                label={({name,percent}) => `${(percent*100).toFixed(0)}%`}
                                labelLine={false}>
                                {licenceStatus.map((d,i) => <Cell key={i} fill={d.fill} />)}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={8} wrapperStyle={{ fontSize:9 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Monthly trend */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>New Assignments / Month</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={monthlyTrend} margin={{ top:0, right:5, left:-20, bottom:0 }}>
                            <XAxis dataKey="month" tick={{ fontSize:8 }} />
                            <YAxis tick={{ fontSize:8 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.green} radius={[3,3,0,0]}>
                                <LabelList dataKey="count" position="top" style={{ fontSize:8 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Unassigned vehicles quick panel */}
            {unassigned.length > 0 && (
                <div style={{ marginTop:12, background:'#fffbe6', border:'1px solid #ffe58f',
                    borderRadius:6, padding:'10px 14px' }}>
                    <div style={{ fontWeight:600, color:'#d48806', fontSize:12, marginBottom:8 }}>
                        🚗 {unassigned.length} vehicle{unassigned.length>1?'s':''} with no Primary driver assigned
                    </div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        {unassigned.slice(0,20).map(v => (
                            <Tag key={v.id} style={{ fontSize:11, cursor:'default' }}>
                                {v.registration_no}
                                <span style={{ color:'#aaa', marginLeft:4, fontSize:10 }}>
                                    {v.vehicle_type}
                                </span>
                            </Tag>
                        ))}
                        {unassigned.length > 20 && (
                            <Tag style={{ fontSize:11 }}>+{unassigned.length-20} more</Tag>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Assignment Form Modal ─────────────────────────────────────────────────────
function AssignmentForm({ open, vehicles, drivers, onSaved, onClose }) {
    const EMPTY = { vehicle_id:null, driver_id:null, role:'Primary',
                    assigned_from:null, assigned_to:null, notes:'' };
    const [form, setForm]     = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [conflict, setConflict] = useState(null);

    useEffect(() => { if (open) setForm(EMPTY); }, [open]);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (!form.vehicle_id) { setConflict(null); return; }
        const veh = vehicles.find(v => v.id === form.vehicle_id);
        if (form.role === 'Primary' && veh?.current_driver_name) {
            setConflict(`Currently assigned: ${veh.current_driver_name} (Primary) — will be ended`);
        } else if (form.role === 'Relief' && veh?.relief_driver_name) {
            setConflict(`Current relief: ${veh.relief_driver_name} — will be replaced`);
        } else {
            setConflict(null);
        }
    }, [form.vehicle_id, form.role]);

    const handleSave = async () => {
        if (!form.vehicle_id)    { message.error('Select a vehicle'); return; }
        if (!form.driver_id)     { message.error('Select a driver');  return; }
        if (!form.assigned_from) { message.error('Enter start date'); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                assigned_from: moment(form.assigned_from).format('YYYY-MM-DD'),
                assigned_to:   form.assigned_to ? moment(form.assigned_to).format('YYYY-MM-DD') : '',
            };
            const res = await FleetService.assignVehicle(payload);
            if (Number(res?.data?.code) === 200) { message.success('Assignment saved'); onSaved(); }
            else message.error(res?.data?.message || 'Failed');
        } catch(e) { message.error(e.message); }
        finally { setSaving(false); }
    };

    const lbl  = { fontSize:12, color:'#666', marginBottom:4 };
    const fbox = { marginBottom:14 };

    return (
        <Modal open={open} onCancel={onClose} width={560} footer={null} destroyOnClose
            title={<div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <UserOutlined style={{ color:C.blue }} />
                <span>Assign Driver to Vehicle</span>
            </div>}>

            {/* Role selector */}
            <div style={{ display:'flex', gap:10, marginBottom:16 }}>
                {['Primary','Relief'].map(r => (
                    <div key={r} onClick={() => set('role', r)} style={{
                        flex:1, padding:'10px 14px', borderRadius:8, cursor:'pointer', textAlign:'center',
                        border:`2px solid ${form.role===r ? C.blue : '#e8e8e8'}`,
                        background: form.role===r ? '#e6f4ff' : '#fafafa', transition:'all .2s',
                    }}>
                        <div style={{ fontWeight:700, color: form.role===r ? C.blue : '#888' }}>{r} Driver</div>
                        <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>
                            {r==='Primary' ? 'Main assigned driver' : 'Backup / relief driver'}
                        </div>
                    </div>
                ))}
            </div>

            {conflict && (
                <div style={{ background:'#fffbe6', border:'1px solid #ffe58f', borderRadius:6,
                    padding:'8px 12px', marginBottom:14, fontSize:12, color:'#d48806' }}>
                    ⚠ {conflict}
                </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
                <div style={fbox}>
                    <div style={lbl}>Vehicle <span style={{ color:'red' }}>*</span></div>
                    <Select showSearch style={{ width:'100%' }} value={form.vehicle_id}
                        onChange={v => set('vehicle_id', v)} placeholder="Select vehicle"
                        optionFilterProp="children">
                        {vehicles.map(v => (
                            <Option key={v.id} value={v.id}>
                                {v.registration_no} — {v.vehicle_type}
                                {v.current_driver_name ? ` (P: ${v.current_driver_name})` : ''}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div style={fbox}>
                    <div style={lbl}>Driver <span style={{ color:'red' }}>*</span></div>
                    <Select showSearch style={{ width:'100%' }} value={form.driver_id}
                        onChange={v => set('driver_id', v)} placeholder="Select driver"
                        optionFilterProp="children">
                        {drivers.map(d => {
                            const licDays = toN(d.licence_days_remaining);
                            return (
                                <Option key={d.id} value={d.id}>
                                    {d.name}
                                    {licDays < 0
                                        ? ' ⛔ Licence EXPIRED'
                                        : licDays < 30 ? ' ⚠ Licence expiring' : ''}
                                </Option>
                            );
                        })}
                    </Select>
                </div>
                <div style={fbox}>
                    <div style={lbl}>Start Date <span style={{ color:'red' }}>*</span></div>
                    <DatePicker style={{ width:'100%' }}
                        value={form.assigned_from ? moment(form.assigned_from) : null}
                        onChange={d => set('assigned_from', d ? d.format('YYYY-MM-DD') : null)}
                        format="DD MMM YYYY" />
                </div>
                <div style={fbox}>
                    <div style={lbl}>End Date <span style={{ fontSize:10, color:'#aaa' }}>(optional)</span></div>
                    <DatePicker style={{ width:'100%' }}
                        value={form.assigned_to ? moment(form.assigned_to) : null}
                        onChange={d => set('assigned_to', d ? d.format('YYYY-MM-DD') : null)}
                        format="DD MMM YYYY"
                        disabledDate={d => form.assigned_from && d.isBefore(moment(form.assigned_from))} />
                </div>
                <div style={{ ...fbox, gridColumn:'1/-1' }}>
                    <div style={lbl}>Notes</div>
                    <Input value={form.notes} onChange={e => set('notes', e.target.value)}
                        placeholder="e.g. Covering for John during leave" />
                </div>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button type="primary" loading={saving} onClick={handleSave}
                    style={{ background:C.blue, borderColor:C.blue }}>
                    Save Assignment
                </Button>
            </div>
        </Modal>
    );
}

// ─── Driver History Modal ──────────────────────────────────────────────────────
function DriverHistoryModal({ driver, onClose }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!driver) return;
        setLoading(true);
        FleetService.getDriverAssignments(driver.id)
            .then(res => {
                if (Number(res?.data?.code) === 200) setHistory(res.data.result || []);
            }).finally(() => setLoading(false));
    }, [driver?.id]);

    const stats = useMemo(() => ({
        total:    history.length,
        totalDays:history.reduce((s,h) => s + toN(h.days_assigned), 0),
        active:   history.filter(h => h.status === 'Active').length,
        vehicles: new Set(history.map(h => h.vehicle_id)).size,
    }), [history]);

    if (!driver) return null;

    const exportHistory = () => {
        const ws = XLSX.utils.json_to_sheet(history.map(h => ({
            Vehicle:        h.registration_no,
            Type:           h.vehicle_name,
            Facility:       h.facility,
            Role:           h.role || 'Primary',
            'Start Date':   h.assigned_from,
            'End Date':     h.assigned_to || (h.status==='Active'?'Ongoing':''),
            'Days Assigned':h.days_assigned,
            Status:         h.status,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'History');
        saveAs(new Blob([XLSX.write(wb,{bookType:'xlsx',type:'array'})]),
            `${driver.name}_assignments.xlsx`);
    };

    return (
        <Modal open={!!driver} onCancel={onClose} width={720} destroyOnClose
            footer={
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <Button icon={<DownloadOutlined />} onClick={exportHistory} disabled={!history.length}>
                        Export XLSX
                    </Button>
                    <Button onClick={onClose}>Close</Button>
                </div>
            }
            title={<div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <HistoryOutlined />
                <span>Assignment History — {driver.name}</span>
            </div>}>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
                {[
                    { label:'Total Assignments', value:stats.total,              color:C.blue   },
                    { label:'Unique Vehicles',   value:stats.vehicles,           color:C.purple },
                    { label:'Total Days',        value:fmtDays(stats.totalDays), color:C.orange },
                    { label:'Currently Active',  value:stats.active,             color:C.green  },
                ].map(({ label, value, color }) => (
                    <div key={label} style={{ background:'#fafafa', border:'1px solid #e8e8e8',
                        borderRadius:8, padding:'10px 12px', borderTop:`3px solid ${color}` }}>
                        <div style={{ fontSize:10, color:'#888' }}>{label}</div>
                        <div style={{ fontSize:18, fontWeight:700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {loading ? (
                <div style={{ textAlign:'center', padding:40, color:'#aaa' }}>Loading history…</div>
            ) : history.length === 0 ? (
                <div style={{ textAlign:'center', padding:40, color:'#aaa' }}>No assignment history found.</div>
            ) : (
                <div style={{ border:'1px solid #e8e8e8', borderRadius:8, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                            <tr style={{ background:'#1a3a5c', color:'#fff' }}>
                                {['Vehicle','Type','Facility','Role','Start','End','Days','Status'].map(h => (
                                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:11, fontWeight:600 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((h,i) => (
                                <tr key={h.id} style={{ background:i%2===0?'#fff':'#f9f9f9', borderBottom:'1px solid #f0f0f0' }}>
                                    <td style={{ padding:'8px 10px', fontWeight:700, color:C.blue }}>{h.registration_no}</td>
                                    <td style={{ padding:'8px 10px', fontSize:11 }}>{h.vehicle_name}</td>
                                    <td style={{ padding:'8px 10px', fontSize:11, color:'#888' }}>{h.facility}</td>
                                    <td style={{ padding:'8px 10px' }}>
                                        <Tag color={h.role==='Primary'?'blue':'purple'} style={{ fontSize:10 }}>{h.role||'Primary'}</Tag>
                                    </td>
                                    <td style={{ padding:'8px 10px', whiteSpace:'nowrap' }}>{fmtD(h.assigned_from)}</td>
                                    <td style={{ padding:'8px 10px', whiteSpace:'nowrap' }}>
                                        {h.assigned_to ? fmtD(h.assigned_to) : (h.status==='Active'?'Ongoing':'—')}
                                    </td>
                                    <td style={{ padding:'8px 10px', fontWeight:600, color:toN(h.days_assigned)>90?C.orange:'#333' }}>
                                        {fmtDays(h.days_assigned)}
                                    </td>
                                    <td style={{ padding:'8px 10px' }}>
                                        <Tag color={h.status==='Active'?'green':'default'} style={{ fontSize:10 }}>{h.status}</Tag>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr style={{ background:'#f0f5ff', borderTop:'2px solid #adc6ff' }}>
                                <td colSpan={6} style={{ padding:'8px 10px', fontWeight:700, fontSize:12, color:'#2f54eb' }}>
                                    TOTAL ({history.length} assignments)
                                </td>
                                <td style={{ padding:'8px 10px', fontWeight:700, color:C.orange }}>
                                    {fmtDays(stats.totalDays)}
                                </td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </Modal>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FleetAssignments() {
    const [assignments,  setAssignments]  = useState([]);
    const [vehicles,     setVehicles]     = useState([]);
    const [drivers,      setDrivers]      = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [formOpen,     setFormOpen]     = useState(false);
    const [histDriver,   setHistDriver]   = useState(null);
    const [showDash,     setShowDash]     = useState(true);
    const [activeTab,    setActiveTab]    = useState('vehicles');
    const [period,       setPeriod]       = useState(DEFAULT_PERIOD);

    // Filters
    const [search,       setSearch]       = useState('');
    const [filterStatus, setFilterStatus] = useState('Active');
    const [filterRole,   setFilterRole]   = useState('');
    const [filterFacility,setFilterFacility]= useState('');
    const [filterRegion, setFilterRegion] = useState('');
    const [filterLicence,setFilterLicence]= useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [aRes, vRes, dRes] = await Promise.all([
                FleetService.reportDrivers(),
                FleetService.getVehiclesList(),
                FleetService.getDriversList(),
            ]);
            if (Number(aRes?.data?.code) === 200) setAssignments(aRes.data.result || []);
            if (Number(vRes?.data?.code) === 200) setVehicles(vRes.data.result   || []);
            if (Number(dRes?.data?.code) === 200) setDrivers(dRes.data.result    || []);
        } catch { message.error('Failed to load assignments'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleEnd = async (id) => {
        try {
            const res = await FleetService.endAssignment(id);
            if (Number(res?.data?.code) === 200) { message.success('Assignment ended'); load(); }
            else message.error(res?.data?.message || 'Failed');
        } catch { message.error('Failed to end assignment'); }
    };

    // Derived stats
    const active   = useMemo(() => assignments.filter(a => a.status === 'Active'), [assignments]);
    const primary  = useMemo(() => active.filter(a => (a.role||'Primary') === 'Primary'), [active]);
    const relief   = useMemo(() => active.filter(a => a.role === 'Relief'), [active]);
    const expiring = useMemo(() => drivers.filter(d => toN(d.licence_days_remaining) >= 0 && toN(d.licence_days_remaining) < 30), [drivers]);
    const expired  = useMemo(() => drivers.filter(d => toN(d.licence_days_remaining) < 0), [drivers]);

    // Vehicle → driver map
    const vehicleDriverMap = useMemo(() => {
        const m = {};
        active.forEach(a => {
            if (!m[a.vehicle_id]) m[a.vehicle_id] = { primary:null, relief:null };
            if ((a.role||'Primary') === 'Primary') m[a.vehicle_id].primary = a;
            else m[a.vehicle_id].relief = a;
        });
        return m;
    }, [active]);

    // Unique facilities for filter dropdown
    const facilities = useMemo(() => [...new Set(assignments.map(a => a.facility).filter(Boolean))].sort(), [assignments]);
    const regions    = useMemo(() => [...new Set(vehicles.map(v => v.region).filter(Boolean))].sort(), [vehicles]);

    // Filter logic
    const filtered = useMemo(() => assignments.filter(a => {
        const q   = search.toLowerCase();
        const drv = drivers.find(d => d.id === a.driver_id);
        const licDays = drv ? toN(drv.licence_days_remaining) : 999;

        const mSearch   = !q || (a.registration_no||'').toLowerCase().includes(q)
                             || (a.driver_name||'').toLowerCase().includes(q)
                             || (a.facility||'').toLowerCase().includes(q);
        const mStatus   = !filterStatus   || a.status   === filterStatus;
        const mRole     = !filterRole     || (a.role||'Primary') === filterRole;
        const mFacility = !filterFacility || a.facility  === filterFacility;
        const mRegion   = !filterRegion   || (() => {
            const veh = vehicles.find(v => v.id === a.vehicle_id);
            return veh?.region === filterRegion;
        })();
        const mLicence  = !filterLicence  || (() => {
            if (filterLicence === 'expired')  return licDays < 0;
            if (filterLicence === 'expiring') return licDays >= 0 && licDays < 30;
            if (filterLicence === 'valid')    return licDays >= 30;
            return true;
        })();

        return mSearch && mStatus && mRole && mFacility && mRegion && mLicence;
    }), [assignments, search, filterStatus, filterRole, filterFacility, filterRegion, filterLicence, drivers, vehicles]);

    const anomalyCount = useMemo(() => detectAnomalies(assignments, drivers).length, [assignments, drivers]);

    // Export history tab
    const exportHistory = () => {
        const ws = XLSX.utils.json_to_sheet(filtered.map(a => ({
            Vehicle:        a.registration_no,
            'Vehicle Type': a.vehicle_type,
            Driver:         a.driver_name,
            Role:           a.role || 'Primary',
            Facility:       a.facility,
            'Start Date':   a.assigned_from,
            'End Date':     a.assigned_to || (a.status==='Active'?'Ongoing':''),
            'Days Assigned':a.days_assigned,
            Status:         a.status,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Assignments');
        saveAs(new Blob([XLSX.write(wb,{bookType:'xlsx',type:'array'})]),
            `Assignments_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    return (
        <div className="page-content">
            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700 }}>Driver Assignments</h4>
                    <div style={{ fontSize:13, color:'#888', marginTop:2 }}>
                        Assign Primary and Relief drivers · Track assignment history and periods
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    <Button icon={<DashboardOutlined />}
                        type={showDash?'primary':'default'} ghost={showDash}
                        onClick={() => setShowDash(p=>!p)}>
                        {showDash ? 'Hide Dashboard' : 'Show Dashboard'}
                        {anomalyCount > 0 && !showDash && (
                            <span style={{ marginLeft:6, background:C.red, color:'#fff',
                                borderRadius:8, padding:'0 5px', fontSize:10 }}>{anomalyCount}</span>
                        )}
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}
                        style={{ background:C.blue, borderColor:C.blue }}>
                        Assign Driver
                    </Button>
                </div>
            </div>

            {/* KPI Cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:12, marginBottom:20 }}>
                {[
                    { label:'Active Assignments',  value: active.length,      color: C.blue   },
                    { label:'Primary Drivers',     value: primary.length,     color: C.green  },
                    { label:'Relief Drivers',      value: relief.length,      color: C.purple },
                    { label:'Unassigned Vehicles', value: vehicles.filter(v=>!v.current_driver_name).length, color: C.amber },
                    { label:'Licence Expiring',    value: expiring.length,    color: C.orange },
                    { label:'Licence Expired',     value: expired.length,     color: expired.length>0 ? C.red : C.grey },
                ].map(({ label, value, color }) => (
                    <div key={label} style={{ background:'#fff', border:'1px solid #e8e8e8',
                        borderRadius:8, padding:'12px 14px', borderTop:`3px solid ${color}` }}>
                        <div style={{ fontSize:10, color:'#888', marginBottom:3 }}>{label}</div>
                        <div style={{ fontSize:22, fontWeight:700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* Licence warnings */}
            {(expiring.length > 0 || expired.length > 0) && (
                <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:8,
                    padding:'10px 16px', marginBottom:16, display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' }}>
                    <span style={{ fontWeight:700, color:C.red, fontSize:13 }}>
                        ⚠ Driver licence issues:
                    </span>
                    {expired.map(d => (
                        <Tag key={`exp-${d.id}`} color="error" style={{ fontSize:11 }}>
                            ⛔ {d.name} — EXPIRED
                        </Tag>
                    ))}
                    {expiring.map(d => (
                        <Tag key={`ing-${d.id}`} color="warning" style={{ fontSize:11 }}>
                            ⚠ {d.name} — {d.licence_days_remaining}d left
                        </Tag>
                    ))}
                </div>
            )}

            {/* Mini Dashboard */}
            {showDash && assignments.length > 0 && (
                <AssignmentsDashboard
                    assignments={assignments}
                    drivers={drivers}
                    vehicles={vehicles}
                    period={period}
                    onPeriodChange={setPeriod}
                />
            )}

            {/* Tabs */}
            <Tabs activeKey={activeTab} onChange={v => { setActiveTab(v); setSearch(''); }}
                style={{ marginBottom:0 }}>
                <TabPane tab={`Active Assignments (${active.length})`}  key="vehicles" />
                <TabPane tab="All History"                               key="history"  />
                <TabPane tab="Driver Overview"                           key="drivers"  />
            </Tabs>

            {/* Filters */}
            <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap',
                background:'#fafafa', border:'1px solid #e8e8e8', borderTop:'none',
                borderRadius:'0 0 6px 6px', padding:'10px 14px' }}>
                <Input prefix={<SearchOutlined style={{ color:'#ccc' }} />}
                    placeholder="Search vehicle, driver, facility…"
                    value={search} onChange={e => setSearch(e.target.value)}
                    allowClear style={{ width:230 }} />

                {/* Status filter — only on history tab */}
                {activeTab === 'history' && (
                    <Select value={filterStatus} onChange={setFilterStatus} style={{ width:130 }}>
                        <Option value="">All statuses</Option>
                        <Option value="Active">Active</Option>
                        <Option value="Ended">Ended</Option>
                    </Select>
                )}

                {/* Role filter */}
                <Select placeholder="Role" value={filterRole||undefined}
                    onChange={setFilterRole} allowClear style={{ width:120 }}>
                    <Option value="Primary">Primary</Option>
                    <Option value="Relief">Relief</Option>
                </Select>

                {/* Facility filter */}
                <Select showSearch placeholder="Facility" value={filterFacility||undefined}
                    onChange={setFilterFacility} allowClear style={{ width:200 }}
                    optionFilterProp="children">
                    {facilities.map(f => <Option key={f} value={f}>{f}</Option>)}
                </Select>

                {/* Region filter */}
                {regions.length > 0 && (
                    <Select placeholder="Region" value={filterRegion||undefined}
                        onChange={setFilterRegion} allowClear style={{ width:130 }}>
                        {regions.map(r => <Option key={r} value={r}>{r}</Option>)}
                    </Select>
                )}

                {/* Licence status filter */}
                <Select placeholder="Licence" value={filterLicence||undefined}
                    onChange={setFilterLicence} allowClear style={{ width:140 }}>
                    <Option value="valid">Licence Valid</Option>
                    <Option value="expiring">Expiring &lt;30d</Option>
                    <Option value="expired">Licence Expired</Option>
                </Select>

                {/* Export button on history tab */}
                {activeTab === 'history' && (
                    <Button icon={<DownloadOutlined />} onClick={exportHistory}
                        disabled={!filtered.length} style={{ marginLeft:'auto' }}>
                        Export XLSX
                    </Button>
                )}

                <div style={{ marginLeft: activeTab !== 'history' ? 'auto' : 0,
                    fontSize:12, color:'#888', alignSelf:'center' }}>
                    {filtered.length} of {assignments.length}
                </div>
            </div>

            {/* ── TAB: Active Vehicles ── */}
            {activeTab === 'vehicles' && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px,1fr))', gap:12 }}>
                    {vehicles.filter(v => {
                        const hasDrivers = vehicleDriverMap[v.id];
                        if (!hasDrivers) {
                            // Only show unassigned if no filters applied or if search matches
                            const q = search.toLowerCase();
                            if (q && !(v.registration_no||'').toLowerCase().includes(q)
                                  && !(v.vehicle_type||'').toLowerCase().includes(q)) return false;
                            if (filterRole || filterStatus === 'Ended') return false;
                        }
                        // Apply facility/region filter
                        if (filterFacility && v.facility !== filterFacility) return false;
                        if (filterRegion   && v.region   !== filterRegion)   return false;
                        return true;
                    }).map(v => {
                        const drv = vehicleDriverMap[v.id];
                        const q   = search.toLowerCase();
                        if (q && drv && !(v.registration_no||'').toLowerCase().includes(q)
                                     && !(drv?.primary?.driver_name||'').toLowerCase().includes(q)
                                     && !(drv?.relief?.driver_name||'').toLowerCase().includes(q)
                                     && !(v.vehicle_type||'').toLowerCase().includes(q)) return null;

                        // Apply role filter
                        if (filterRole === 'Primary' && !drv?.primary) return null;
                        if (filterRole === 'Relief'  && !drv?.relief)  return null;

                        // Apply licence filter to the assigned driver
                        if (filterLicence && drv) {
                            const primaryDrv = drivers.find(d => d.id === drv?.primary?.driver_id);
                            const licDays = primaryDrv ? toN(primaryDrv.licence_days_remaining) : 999;
                            if (filterLicence === 'expired'  && licDays >= 0)  return null;
                            if (filterLicence === 'expiring' && (licDays < 0 || licDays >= 30)) return null;
                            if (filterLicence === 'valid'    && licDays < 30)  return null;
                        }

                        return (
                            <div key={v.id} style={{ background:'#fff', border:'1px solid #e8e8e8',
                                borderRadius:8, padding:'14px 16px',
                                borderLeft:`3px solid ${drv ? C.green : '#e8e8e8'}` }}>
                                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                                    <div>
                                        <div style={{ fontWeight:700, color:C.blue, fontSize:14 }}>
                                            {v.registration_no}
                                        </div>
                                        <div style={{ fontSize:11, color:'#aaa' }}>
                                            {v.vehicle_type} · {v.facility}
                                            {v.region && <span style={{ color:'#722ed1', marginLeft:4 }}>({v.region})</span>}
                                        </div>
                                    </div>
                                    <Button size="small" icon={<PlusOutlined />} type="link"
                                        onClick={() => setFormOpen(true)}>Assign</Button>
                                </div>

                                {/* Primary driver */}
                                {drv?.primary ? (
                                    <div style={{ display:'flex', justifyContent:'space-between',
                                        alignItems:'center', background:'#f0f5ff',
                                        borderRadius:6, padding:'6px 10px', marginBottom:6 }}>
                                        <div>
                                            <Tag color="blue" style={{ fontSize:10 }}>Primary</Tag>
                                            <span style={{ fontWeight:600, fontSize:12 }}>{drv.primary.driver_name}</span>
                                            <div style={{ fontSize:10, color:'#888', marginTop:2 }}>
                                                Since {fmtD(drv.primary.assigned_from)} · {fmtDays(drv.primary.days_assigned)}
                                            </div>
                                        </div>
                                        <Popconfirm title="End this assignment?"
                                            onConfirm={() => handleEnd(drv.primary.id)}
                                            okText="End" okButtonProps={{ danger:true }}>
                                            <Button size="small" danger icon={<CloseOutlined />} />
                                        </Popconfirm>
                                    </div>
                                ) : (
                                    <div style={{ background:'#fafafa', borderRadius:6, padding:'6px 10px',
                                        marginBottom:6, fontSize:11, color:'#aaa', textAlign:'center' }}>
                                        No Primary driver assigned
                                    </div>
                                )}

                                {/* Relief driver */}
                                {drv?.relief ? (
                                    <div style={{ display:'flex', justifyContent:'space-between',
                                        alignItems:'center', background:'#f9f0ff',
                                        borderRadius:6, padding:'6px 10px' }}>
                                        <div>
                                            <Tag color="purple" style={{ fontSize:10 }}>Relief</Tag>
                                            <span style={{ fontWeight:600, fontSize:12 }}>{drv.relief.driver_name}</span>
                                            <div style={{ fontSize:10, color:'#888', marginTop:2 }}>
                                                Since {fmtD(drv.relief.assigned_from)} · {fmtDays(drv.relief.days_assigned)}
                                            </div>
                                        </div>
                                        <Popconfirm title="End relief assignment?"
                                            onConfirm={() => handleEnd(drv.relief.id)}
                                            okText="End" okButtonProps={{ danger:true }}>
                                            <Button size="small" danger icon={<CloseOutlined />} />
                                        </Popconfirm>
                                    </div>
                                ) : drv?.primary ? (
                                    <div style={{ background:'#fafafa', borderRadius:6, padding:'6px 10px',
                                        fontSize:11, color:'#aaa', textAlign:'center', cursor:'pointer' }}
                                        onClick={() => setFormOpen(true)}>
                                        + Add relief driver
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── TAB: History ── */}
            {activeTab === 'history' && (
                <div style={{ border:'1px solid #e8e8e8', borderRadius:8, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                            <tr style={{ background:'#1a3a5c', color:'#fff' }}>
                                {['Vehicle','Driver','Role','Facility','Region','Start','End','Days','Status','Action'].map(h => (
                                    <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:600 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((a,i) => {
                                const drv = drivers.find(d => d.id === a.driver_id);
                                const licDays = drv ? toN(drv.licence_days_remaining) : 999;
                                const veh = vehicles.find(v => v.id === a.vehicle_id);
                                return (
                                    <tr key={a.id} style={{
                                        background: i%2===0 ? '#fff' : '#f9f9f9',
                                        borderBottom:'1px solid #f0f0f0',
                                        borderLeft: licDays < 0 ? `3px solid ${C.red}` : '3px solid transparent',
                                    }}>
                                        <td style={{ padding:'10px 12px', fontWeight:700, color:C.blue }}>
                                            {a.registration_no}
                                        </td>
                                        <td style={{ padding:'10px 12px', fontWeight:600 }}>
                                            {a.driver_name}
                                            {licDays < 0 && (
                                                <span style={{ fontSize:10, color:C.red, marginLeft:5 }}>⛔ Expired</span>
                                            )}
                                            {licDays >= 0 && licDays < 30 && (
                                                <span style={{ fontSize:10, color:C.orange, marginLeft:5 }}>⚠ {licDays}d</span>
                                            )}
                                        </td>
                                        <td style={{ padding:'10px 12px' }}>
                                            <Tag color={(a.role||'Primary')==='Primary'?'blue':'purple'} style={{ fontSize:10 }}>
                                                {a.role||'Primary'}
                                            </Tag>
                                        </td>
                                        <td style={{ padding:'10px 12px', fontSize:11, color:'#888' }}>{a.facility}</td>
                                        <td style={{ padding:'10px 12px', fontSize:11, color:'#722ed1' }}>
                                            {veh?.region || '—'}
                                        </td>
                                        <td style={{ padding:'10px 12px', whiteSpace:'nowrap' }}>{fmtD(a.assigned_from)}</td>
                                        <td style={{ padding:'10px 12px', whiteSpace:'nowrap' }}>
                                            {a.assigned_to ? fmtD(a.assigned_to) : (a.status==='Active' ? 'Ongoing' : '—')}
                                        </td>
                                        <td style={{ padding:'10px 12px', fontWeight:600,
                                            color: toN(a.days_assigned)>365?C.red:toN(a.days_assigned)>90?C.orange:'#333' }}>
                                            {fmtDays(a.days_assigned)}
                                        </td>
                                        <td style={{ padding:'10px 12px' }}>
                                            <Tag color={a.status==='Active'?'green':'default'} style={{ fontSize:10 }}>
                                                {a.status}
                                            </Tag>
                                        </td>
                                        <td style={{ padding:'10px 12px' }}>
                                            {a.status === 'Active' && (
                                                <Popconfirm title="End this assignment?"
                                                    onConfirm={() => handleEnd(a.id)}
                                                    okText="End" okButtonProps={{ danger:true }}>
                                                    <Button size="small" danger>End</Button>
                                                </Popconfirm>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background:'#f0f5ff', borderTop:'2px solid #adc6ff' }}>
                                    <td colSpan={7} style={{ padding:'8px 12px', fontWeight:700, fontSize:12, color:'#2f54eb' }}>
                                        TOTAL ({filtered.length} records)
                                    </td>
                                    <td style={{ padding:'8px 12px', fontWeight:700, color:C.orange }}>
                                        {fmtDays(filtered.reduce((s,a) => s + toN(a.days_assigned), 0))}
                                    </td>
                                    <td colSpan={2} />
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}

            {/* ── TAB: Drivers ── */}
            {activeTab === 'drivers' && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:12 }}>
                    {drivers.filter(d => {
                        const q = search.toLowerCase();
                        const mSearch = !q || (d.name||'').toLowerCase().includes(q);
                        const licDays = toN(d.licence_days_remaining);
                        const mLicence = !filterLicence
                            || (filterLicence==='expired'  && licDays < 0)
                            || (filterLicence==='expiring' && licDays >= 0 && licDays < 30)
                            || (filterLicence==='valid'    && licDays >= 30);
                        return mSearch && mLicence;
                    }).map(d => {
                        const licDays = toN(d.licence_days_remaining);
                        return (
                            <div key={d.id} style={{ background:'#fff', border:'1px solid #e8e8e8',
                                borderRadius:8, padding:'14px 16px',
                                borderLeft:`3px solid ${licDays<0?C.red:licDays<30?C.amber:'#e8e8e8'}` }}>
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                                    <div>
                                        <div style={{ fontWeight:700, fontSize:13 }}>{d.name}</div>
                                        <div style={{ fontSize:11, color:'#aaa' }}>{d.role_name}</div>
                                    </div>
                                    <Button size="small" icon={<HistoryOutlined />} type="link"
                                        onClick={() => setHistDriver(d)}>History</Button>
                                </div>
                                <div style={{ marginTop:8, fontSize:11, display:'grid', gridTemplateColumns:'1fr 1fr', gap:4 }}>
                                    <div>
                                        <span style={{ color:'#aaa' }}>Current Vehicle: </span>
                                        <span style={{ fontWeight:600, color: d.current_vehicle_reg ? C.blue : '#aaa' }}>
                                            {d.current_vehicle_reg || 'None'}
                                        </span>
                                    </div>
                                    <div>
                                        <span style={{ color:'#aaa' }}>Licence: </span>
                                        <span style={{ fontWeight:600,
                                            color: licDays<0?C.red:licDays<30?C.orange:C.green }}>
                                            {licDays<0
                                                ? `Expired ${Math.abs(licDays)}d ago`
                                                : licDays<30 ? `${licDays}d left`
                                                : fmtD(d.licence_expiry)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <AssignmentForm open={formOpen} vehicles={vehicles} drivers={drivers}
                onSaved={() => { setFormOpen(false); load(); }}
                onClose={() => setFormOpen(false)} />
            <DriverHistoryModal driver={histDriver} onClose={() => setHistDriver(null)} />
        </div>
    );
}